// Global Instructions Rule Applied!
//
// Fee Periods editor (funds Terms tab): the sequenced management-fee
// schedule (fund_fee_periods). Each row: term + units, base fee %, basis,
// reduction scheme (No reduction / Percentage / Fixed), reduction %,
// reduction frequency (quarters), fee floor %. Sequence follows the row
// order; the whole set saves in one full-replacement round-trip
// (POST /funds/:id/fee-periods -> fund__save_fee_periods). Rates display as
// percentages and store as decimals (TermsTab percentField convention).
// "Applies From" is read-only: the first day each SAVED period is billed
// (view__fund_terms prog__applies_from) - its term start, or the next
// billing-period start when the fund's Fee Period Switch defers it.

import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, InputNumber, Select, Table, Typography } from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import { showErrorToast } from '../../../../../core/utils/errorToast.tsx'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store } from '../../../../../state_manager/store.ts'
import { decimalToPercent, percentToDecimal } from '../../../../../core/utils/inputNumberFormat.ts'
import { saveFundFeePeriods, FundFeePeriodPayload } from '../../../services/fundsService.ts'
import type { FundTermsRecord } from '../../../../../state_manager/data_models/fund_terms_record.ts'

const { Text } = Typography

const NO_REDUCTION_LABEL = 'No reduction'
// the Terms tab date format (fundTermsFieldConfigs DATE_FORMAT)
const DATE_FORMAT = 'MM/DD/YYYY'

interface FeePeriodRow {
  key: string
  id?: string
  term: number | null
  term_units_id: number | null
  base_fee_rate: number | null
  basis_id: number | null
  reduction_scheme_id: number | null
  reduction_rate: number | null
  reduction_frequency_quarters: number | null
  fee_floor_rate: number | null
}

let rowKeySeq = 0
const nextRowKey = () => `fee-period-${++rowKeySeq}`

interface FeePeriodsEditorProps {
  fundId: string
  terms: FundTermsRecord
  onSaved: () => void
}

export default function FeePeriodsEditor({ fundId, terms, onSaved }: FeePeriodsEditorProps) {
  const timeUnits = useSelector((state: any) => state.globalConfigs?.time_units ?? [])
  const feeBases = useSelector((state: any) => state.globalConfigs?.fee_bases ?? [])
  const reductionSchemes = useSelector((state: any) => state.globalConfigs?.fee_reduction_schemes ?? [])

  const [rows, setRows] = useState<FeePeriodRow[]>([])
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setRows(
      (terms.fee_periods ?? []).map((p) => ({
        key: p.id,
        id: p.id,
        term: p.term ?? null,
        term_units_id: p.term_units_id ?? null,
        base_fee_rate: p.base_fee_rate ?? null,
        basis_id: p.basis_id ?? null,
        reduction_scheme_id: p.reduction_scheme_id ?? null,
        reduction_rate: p.reduction_rate ?? null,
        reduction_frequency_quarters: p.reduction_frequency_quarters ?? null,
        fee_floor_rate: p.fee_floor_rate ?? null
      }))
    )
    setDirty(false)
  }, [terms])

  const unitOptions = useMemo(() => timeUnits.map((u: any) => ({ value: u.id, label: u.label })), [timeUnits])
  const basisOptions = useMemo(() => feeBases.map((b: any) => ({ value: b.id, label: b.label })), [feeBases])
  const schemeOptions = useMemo(
    () => reductionSchemes.map((s: any) => ({ value: s.id, label: s.label })),
    [reductionSchemes]
  )
  const noReductionId = useMemo(
    () => reductionSchemes.find((s: any) => s.label === NO_REDUCTION_LABEL)?.id ?? null,
    [reductionSchemes]
  )

  const updateRow = (key: string, patch: Partial<FeePeriodRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)))
    setDirty(true)
  }
  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        key: nextRowKey(),
        term: null,
        term_units_id: null,
        base_fee_rate: null,
        basis_id: null,
        reduction_scheme_id: noReductionId,
        reduction_rate: null,
        reduction_frequency_quarters: null,
        fee_floor_rate: null
      }
    ])
    setDirty(true)
  }
  const removeRow = (key: string) => {
    setRows((prev) => prev.filter((r) => r.key !== key))
    setDirty(true)
  }

  // first billed day per saved period; an unsaved row, or edits not saved
  // yet, have none to show until the periods are saved
  const appliesFromById = useMemo(
    () => new Map((terms.fee_periods ?? []).map((p) => [p.id, p.prog__applies_from ?? null])),
    [terms.fee_periods]
  )

  const isNoReduction = (row: FeePeriodRow) =>
    noReductionId != null && row.reduction_scheme_id === noReductionId

  const handleSave = async () => {
    const invalid = rows.find(
      (r) => r.term == null || !r.term_units_id || r.base_fee_rate == null || !r.basis_id || !r.reduction_scheme_id
    )
    if (invalid) {
      showErrorToast('Every fee period needs a term, units, base fee, basis and reduction scheme')
      return
    }
    const invalidReduction = rows.find(
      (r) =>
        !isNoReduction(r) &&
        (r.reduction_rate == null || r.reduction_frequency_quarters == null || r.reduction_frequency_quarters <= 0)
    )
    if (invalidReduction) {
      showErrorToast('Percentage/Fixed schemes need a reduction % and a positive frequency (quarters)')
      return
    }

    setSaving(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const payload: FundFeePeriodPayload[] = rows.map((r) => ({
        ...(r.id ? { id: r.id } : {}),
        term: r.term,
        term_units_id: r.term_units_id,
        base_fee_rate: r.base_fee_rate,
        basis_id: r.basis_id,
        reduction_scheme_id: r.reduction_scheme_id,
        reduction_rate: isNoReduction(r) ? null : r.reduction_rate,
        reduction_frequency_quarters: isNoReduction(r) ? null : r.reduction_frequency_quarters,
        fee_floor_rate: r.fee_floor_rate
      }))
      const res = await saveFundFeePeriods(fundId, payload, token)
      if (res.error) {
        showErrorToast(res.error)
        return
      }
      toast.success('Fee periods saved')
      setDirty(false)
      onSaved()
    } finally {
      setSaving(false)
    }
  }

  // percent columns edit as percentages, store as decimals
  const percentInput = (row: FeePeriodRow, field: keyof FeePeriodRow, precision = 4, disabled = false) => (
    <InputNumber
      size='small'
      className='w-full'
      value={decimalToPercent(row[field] as number | null)}
      precision={precision}
      suffix='%'
      disabled={disabled}
      onChange={(v) => updateRow(row.key, { [field]: percentToDecimal(v) } as Partial<FeePeriodRow>)}
    />
  )

  const columns = [
    {
      title: '#',
      dataIndex: 'sequence',
      width: 40,
      render: (_: unknown, __: FeePeriodRow, index: number) => index + 1
    },
    {
      title: 'Applies From',
      dataIndex: 'applies_from',
      width: 110,
      render: (_: unknown, row: FeePeriodRow) => {
        const appliesFrom = !dirty && row.id ? appliesFromById.get(row.id) : null
        return <Text type='secondary'>{appliesFrom ? dayjs(appliesFrom).format(DATE_FORMAT) : '—'}</Text>
      }
    },
    {
      title: 'Term',
      dataIndex: 'term',
      width: 90,
      render: (_: unknown, row: FeePeriodRow) => (
        <InputNumber
          size='small'
          className='w-full'
          value={row.term}
          min={1}
          precision={0}
          onChange={(v) => updateRow(row.key, { term: v == null ? null : Number(v) })}
        />
      )
    },
    {
      title: 'Units',
      dataIndex: 'term_units_id',
      width: 110,
      render: (_: unknown, row: FeePeriodRow) => (
        <Select
          size='small'
          className='w-full'
          value={row.term_units_id}
          options={unitOptions}
          placeholder='Units...'
          onChange={(v) => updateRow(row.key, { term_units_id: v })}
        />
      )
    },
    {
      title: 'Base Fee',
      dataIndex: 'base_fee_rate',
      width: 110,
      render: (_: unknown, row: FeePeriodRow) => percentInput(row, 'base_fee_rate')
    },
    {
      title: 'Basis',
      dataIndex: 'basis_id',
      width: 160,
      render: (_: unknown, row: FeePeriodRow) => (
        <Select
          size='small'
          className='w-full'
          value={row.basis_id}
          options={basisOptions}
          placeholder='Basis...'
          onChange={(v) => updateRow(row.key, { basis_id: v })}
        />
      )
    },
    {
      title: 'Reduction Scheme',
      dataIndex: 'reduction_scheme_id',
      width: 140,
      render: (_: unknown, row: FeePeriodRow) => (
        <Select
          size='small'
          className='w-full'
          value={row.reduction_scheme_id}
          options={schemeOptions}
          placeholder='Scheme...'
          onChange={(v) => updateRow(row.key, { reduction_scheme_id: v })}
        />
      )
    },
    {
      title: 'Reduction',
      dataIndex: 'reduction_rate',
      width: 110,
      render: (_: unknown, row: FeePeriodRow) => percentInput(row, 'reduction_rate', 4, isNoReduction(row))
    },
    {
      title: 'Freq (Qtrs)',
      dataIndex: 'reduction_frequency_quarters',
      width: 90,
      render: (_: unknown, row: FeePeriodRow) => (
        <InputNumber
          size='small'
          className='w-full'
          value={row.reduction_frequency_quarters}
          min={1}
          precision={0}
          disabled={isNoReduction(row)}
          onChange={(v) => updateRow(row.key, { reduction_frequency_quarters: v == null ? null : Number(v) })}
        />
      )
    },
    {
      title: 'Fee Floor',
      dataIndex: 'fee_floor_rate',
      width: 110,
      render: (_: unknown, row: FeePeriodRow) => percentInput(row, 'fee_floor_rate')
    },
    {
      title: '',
      dataIndex: 'actions',
      width: 40,
      render: (_: unknown, row: FeePeriodRow) => (
        <Button type='text' size='small' danger icon={<DeleteOutlined />} onClick={() => removeRow(row.key)} />
      )
    }
  ]

  return (
    <div>
      <div className='flex items-center justify-between mb-2'>
        <Text type='secondary'>
          Periods apply in sequence from the fee schedule anchor and no fee accrues after the last
          period ends — add a period to keep charging. Side-letter rates scale off the headline rate.
        </Text>
        <div className='flex gap-2'>
          <Button size='small' icon={<PlusOutlined />} onClick={addRow}>
            Add Period
          </Button>
          <Button size='small' type='primary' disabled={!dirty} loading={saving} onClick={handleSave}>
            Save Periods
          </Button>
        </div>
      </div>
      {rows.length === 0 ? (
        <Text type='secondary'>
          No fee periods — the engine falls back to the flat headline rate and basis above.
        </Text>
      ) : (
        <Table size='small' columns={columns} dataSource={rows} pagination={false} rowKey='key' />
      )}
    </div>
  )
}
